import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Parameters} from 'platform/components';
import {Box, Grid, HStack, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber, useFormatPercentage} from 'platform/locale';
import {TooltipProps} from 'recharts';
import {match, Pattern} from 'ts-pattern';

import {filter, isNotNil} from 'ramda';

import {SourcingVehicleResponseBody, useSourcingVehicleDetailQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, TestIdProps, isNotNilOrEmpty, suffixTestId} from 'shared/web';

import {VehicleImage} from '../../../../../../../components/VehicleImage/VehicleImage';
import {useSourcingCurrency} from '../../../../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../../../../hooks/useSourcingTargetCountry';
import {PriceMapChartItem} from '../types/PriceMapChartItem';
import {getDifference} from '../utils/getDifference';
import {ComparedValue} from './ComparedValue';

export function ChartTooltip(
  // TooltipProps comes from recharts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: TooltipProps<any, any> & {
    mySourcingVehicle: SourcingVehicleResponseBody;
  } & TestIdProps
) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const formatPercentage = useFormatPercentage();
  const payload: PriceMapChartItem | Nullish = props.payload?.[0]?.payload;

  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code;
  const {
    data: vehicle,
    isFetching,
    isError,
  } = useSourcingVehicleDetailQuery(
    isNotNil(payload?.car.ad_id)
      ? {externalId: payload.car.ad_id, currency, buyerCountry: country}
      : skipToken
  );

  const vehicleName = match(vehicle?.sourcingVehicle?.adTitle)
    .with(Pattern.nonNullable, () => vehicle?.sourcingVehicle?.adTitle)
    .otherwise(
      () =>
        `${vehicle?.sourcingVehicle?.make?.translation} ${vehicle?.sourcingVehicle?.model?.translation}`
    );

  const vehicleDotListItems: string[] = filter(isNotNilOrEmpty, [
    vehicle?.sourcingVehicle?.transmission?.translation,
    vehicle?.sourcingVehicle?.fuelType?.translation,
    isNotNil(vehicle?.sourcingVehicle?.power)
      ? `${vehicle.sourcingVehicle.power} ${i18n.t('general.metric.kW')}`
      : null,
    vehicle?.sourcingVehicle?.driveType?.translation,
    vehicle?.sourcingVehicle?.carStyle?.translation,
  ]);

  const yearDifference = getDifference(
    vehicle?.sourcingVehicle?.year,
    props.mySourcingVehicle.year
  );
  const featureScoreDifference = getDifference(
    vehicle?.sourcingVehicle?.featureScore ?? 0,
    props.mySourcingVehicle.featureScore
  );
  const mileageDifference = getDifference(
    vehicle?.sourcingVehicle?.mileage,
    props.mySourcingVehicle.mileage
  );
  const vehiclePrice = vehicle?.sourcingVehicle?.price?.withVat ?? 0;
  const priceDifference = getDifference(vehiclePrice, props.mySourcingVehicle.price?.withVat ?? 0);
  const vehicleCurrency = vehicle?.sourcingVehicle?.price?.currency?.key ?? 'CZK';

  const advertisementDotListItems: string[] = filter(isNotNilOrEmpty, [
    i18n.t('entity.vehicle.labels.nDaysInAdvertisement', {
      count: vehicle?.sourcingVehicle?.daysOnStock,
    }),
    vehicle?.sourcingVehicle?.seller?.type?.translation,
  ]);

  return (
    <Box
      backgroundColor="general.white"
      borderRadius="medium"
      boxShadow="elevation_3"
      border="1px solid"
      borderColor="palettes.neutral.40.100"
      overflow="hidden"
      minWidth={130}
    >
      <DataStatus isLoading={isFetching} isError={isError} minHeight={34}>
        <HStack height="100%">
          <VehicleImage vehicle={vehicle} width={45} height={34} />
          <Box padding={4} paddingBottom={2} height={34}>
            <VStack justify="space-between" height="100%">
              <Box flexGrow={1}>
                <Text size="small" alternative>
                  {vehicleName}
                </Text>
                <Parameters
                  parameters={vehicleDotListItems}
                  color="secondary"
                  size="xSmall"
                  data-testid={suffixTestId('vehicleParameters', props)}
                />
                <Space vertical={1} />
                <Grid spacing={4} columns={4}>
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.year ?? 0}
                    difference={yearDifference}
                    data-testid={suffixTestId('year', props)}
                  />
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.featureScore ?? 0}
                    difference={featureScoreDifference}
                    rankValue={(vehicle?.sourcingVehicle?.featureScore ?? 0) * 100}
                    formatter={(value) => formatPercentage(value, 'down')}
                    data-testid={suffixTestId('featureScore', props)}
                  />
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.mileage ?? 0}
                    difference={mileageDifference}
                    formatter={(value) => `${formatNumber(value)} ${i18n.t('general.metric.km')}`}
                    isReverseColor
                    data-testid={suffixTestId('mileage', props)}
                  />
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.price?.withVat ?? 0}
                    difference={priceDifference}
                    firstScrape={vehicle?.sourcingVehicle?.priceChanges?.[0]}
                    formatter={(value) => formatCurrency(value, vehicleCurrency)}
                    firstScrapeFormatter={(value) => formatCurrency(value, vehicleCurrency)}
                    isReverseColor
                    isAlignEnd
                    data-testid={suffixTestId('price', props)}
                  />
                </Grid>
              </Box>
              <Parameters
                parameters={advertisementDotListItems}
                color="secondary"
                size="xSmall"
                data-testid={suffixTestId('advertisementVehicleParameters', props)}
              />
            </VStack>
          </Box>
        </HStack>
      </DataStatus>
    </Box>
  );
}
